/**
 * useCampaignVideoControls — write-side for the live-video popup that opens
 * from the Confirmed tab, the Live Content tab, and the reporting Creator
 * Content tab.
 *
 * Mirrors the other "review controls" hooks: open writes the URL AND sets the
 * caller's local state in one call; close clears both.
 *
 *   openVideo(row, id)  → ?panel=video&video=<id>
 *   closeVideo()        → clears panel/video
 *
 * `id` is the roster_entry_id (stable across all three surfaces). Empty / falsy
 * id opens locally only — matches the keyless-creator guard pattern used
 * elsewhere; rare for live videos but possible if BE data is incomplete.
 *
 * Generic over the local-state shape so each surface can carry its own row
 * type (RosterEntryFinalized / LiveVideoRow / { entry }).
 */
import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

export function useCampaignVideoControls<T>(
  setLocalState: (next: T | null) => void,
) {
  const navigate = useNavigate();

  const openVideo = useCallback(
    (row: T, id: string | undefined) => {
      setLocalState(row);
      const videoId = id?.trim();
      if (!videoId) return;
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          panel: 'video',
          video: videoId,
          // Clear unrelated review-drawer params, but KEEP `sub` — the video
          // popup overlays the reporting Creator Content sub-tab, so dropping
          // `sub` would reset the tab back to General Insights on click.
          creator: undefined,
          thread: undefined,
        })),
      } as never);
    },
    [navigate, setLocalState],
  );

  const closeVideo = useCallback(() => {
    setLocalState(null);
    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        panel: undefined,
        video: undefined,
      })),
    } as never);
  }, [navigate, setLocalState]);

  return { openVideo, closeVideo };
}
